import React, { createContext, useContext, useState, useEffect } from 'react';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

import { INITIAL_DEMO_ACCOUNTS } from '../lib/demoData';

const AccountContext = createContext();

export const useAccounts = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccounts must be used within an AccountProvider');
  }
  return context;
};

const DEMO_ACCOUNTS_KEY = 'spend_easy_demo_accounts';

const loadDemoAccounts = () => {
  try {
    const saved = localStorage.getItem(DEMO_ACCOUNTS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length >= 5) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading demo accounts:', e);
  }
  saveDemoAccounts(INITIAL_DEMO_ACCOUNTS);
  return INITIAL_DEMO_ACCOUNTS;
};

const saveDemoAccounts = (accs) => {
  try {
    localStorage.setItem(DEMO_ACCOUNTS_KEY, JSON.stringify(accs));
  } catch (e) {
    console.error('Error saving demo accounts:', e);
  }
};

export const AccountProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setAccounts([]);
      setLoading(false);
      return;
    }

    if (currentUser.isGuest) {
      const updateGuestData = () => {
        setAccounts(loadDemoAccounts());
      };
      updateGuestData();
      setLoading(false);
      window.addEventListener('spend_easy_demo_data_updated', updateGuestData);
      return () => {
        window.removeEventListener('spend_easy_demo_data_updated', updateGuestData);
      };
    }

    const accountsRef = collection(db, 'users', currentUser.uid, 'accounts');
    const q = query(accountsRef, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const accountsList = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setAccounts(accountsList);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching accounts:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser]);

  const addAccount = async (accountData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const newAcc = {
        ...accountData,
        id: `demo-acc-${Date.now()}`,
        userId: currentUser.uid,
        createdAt: new Date()
      };
      const updated = [newAcc, ...accounts];
      setAccounts(updated);
      saveDemoAccounts(updated);
      return { id: newAcc.id };
    }

    const accountsRef = collection(db, 'users', currentUser.uid, 'accounts');
    const docRef = await addDoc(accountsRef, {
      ...accountData,
      userId: currentUser.uid,
      createdAt: serverTimestamp()
    });
    return docRef;
  };

  const updateAccount = async (accountId, accountData) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = accounts.map(a => {
        if (a.id === accountId) {
          return { ...a, ...accountData, updatedAt: new Date() };
        }
        return a;
      });
      setAccounts(updated);
      saveDemoAccounts(updated);
      return;
    }

    const accountRef = doc(db, 'users', currentUser.uid, 'accounts', accountId);
    await updateDoc(accountRef, { ...accountData, updatedAt: serverTimestamp() });
  };

  const deleteAccount = async (accountId) => {
    if (!currentUser) throw new Error('No user logged in');

    if (currentUser.isGuest) {
      const updated = accounts.filter(a => a.id !== accountId);
      setAccounts(updated);
      saveDemoAccounts(updated);
      return;
    }

    const accountRef = doc(db, 'users', currentUser.uid, 'accounts', accountId);
    await deleteDoc(accountRef);
  };

  const value = {
    accounts,
    addAccount,
    updateAccount,
    deleteAccount,
    loading
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
};
